import { useQuery } from "@tanstack/react-query";
import { DataAttributionNote } from "@/components/DataAttributionNote";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getDashboard } from "@/api/dashboard";
import { getBackHistory } from "@/api/back";
import { Card, SectionLabel } from "@/components/ui/Card";
import { CHART, SERIES } from "@/lib/theme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
);

export default function Stats() {
  const { t } = useTranslation();
  const dashQ = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard });
  const backQ = useQuery({
    queryKey: ["back-history"],
    queryFn: () => getBackHistory(90),
  });

  if (dashQ.isLoading) {
    return <div className="py-10 text-center text-taupe">{t("stats.loading")}</div>;
  }
  const dash = dashQ.data!;
  const pmc = dash.pmc.last_30_days;

  const pmcData = {
    labels: pmc.map((p) => p.date.slice(5)),
    datasets: [
      {
        label: "CTL",
        data: pmc.map((p) => p.ctl),
        borderColor: CHART.seriesPrimary,
        backgroundColor: "rgba(26,43,66,0.08)",
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: "ATL",
        data: pmc.map((p) => p.atl),
        borderColor: CHART.seriesSecondary,
        backgroundColor: "rgba(212,154,63,0.08)",
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: "TSB",
        data: pmc.map((p) => p.tsb),
        borderColor: SERIES.tsb,
        backgroundColor: "rgba(196,77,58,0.0)",
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        borderDash: [4, 3],
      },
    ],
  };

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>{t("stats.section_title")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium">{t("stats.h1_pmc")}</h1>
      </div>

      <Card>
        <div className="h-64 sm:h-80">
          <Line
            data={pmcData}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              scales: {
                x: { grid: { display: false }, ticks: { color: CHART.tick, maxTicksLimit: 8 } },
                y: { grid: { color: CHART.grid }, ticks: { color: CHART.tick } },
              },
              plugins: {
                legend: {
                  labels: { color: CHART.legend, font: { family: "Inter", size: 12 } },
                  position: "bottom",
                },
                tooltip: {
                  backgroundColor: CHART.tooltipBg,
                  titleColor: CHART.tooltipText,
                  bodyColor: CHART.tooltipText,
                },
              },
            }}
          />
        </div>
      </Card>

      <Card>
        <SectionLabel>{t("stats.current_metrics")}</SectionLabel>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            { label: "CTL", value: Math.round(dash.pmc.current_ctl) },
            { label: "ATL", value: Math.round(dash.pmc.current_atl) },
            { label: "TSB", value: Math.round(dash.pmc.current_tsb) },
          ].map((m) => (
            <div key={m.label} className="rounded-lg bg-cream px-3 py-2">
              <div className="section-label">{m.label}</div>
              <div className="metric-number text-[22px]">{m.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {!backQ.isLoading && backQ.data && backQ.data.count > 0 ? (
        <Card>
          <SectionLabel>{t("stats.back_recent_checkins")}</SectionLabel>
          <ul className="mt-3 space-y-1.5 text-[13px]">
            {backQ.data.entries.slice(-10).reverse().map((e) => (
              <li key={e.date} className="flex items-center justify-between gap-3">
                <span className="text-taupe">{e.date}</span>
                <span
                  className={
                    e.pain_score >= 5
                      ? "metric-number text-coral"
                      : e.pain_score >= 3
                        ? "metric-number text-amber-dark"
                        : "metric-number"
                  }
                >
                  {t("stats.pain_label")} {e.pain_score}/10
                </span>
                <span className="text-taupe">{t("stats.stiffness_label")} {e.morning_stiffness_minutes} {t("duration.min")}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <DataAttributionNote />
    </div>
  );
}
